import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { EvidencePackage, PackageFileKey } from "../../types/evidence";
import { SECTION_ROWS } from "./sectionRows";
import { FILE_SWATCHES } from "./packageFiles";
import { formatFileSize } from "../../utils/formatFileSize";

type Row = { key: string; swatch: string; label: string; caption: string; overLimit: string | null; bytes: number | null; pages: number | null; whole: boolean; onDownload: () => void };

export default function EvidencePackageDialog({ open, providerLabel, pkg, pdfBytes, pdfPages, onDownload, onDownloadPdf, onClose }: {
  open: boolean;
  providerLabel: string;
  pkg: EvidencePackage;
  pdfBytes: number | null;
  pdfPages: number | null;
  onDownload: (file: PackageFileKey) => void;
  onDownloadPdf: () => void;
  onClose: () => void;
}) {
  const problems = [...pkg.files.map((f) => f.overLimit), pkg.total.overLimit].filter((p): p is string => Boolean(p));
  const rows: Row[] = [...pkg.files].sort((a, b) => Number(Boolean(b.whole)) - Number(Boolean(a.whole))).map((f) => ({
    key: f.file,
    swatch: FILE_SWATCHES[f.file],
    label: f.label,
    caption: f.whole ? (f.slot ? `All sections · Upload as ${f.slot}` : "All sections") : f.sections.map((s) => SECTION_ROWS[s].heading).join(", "),
    overLimit: f.overLimit,
    bytes: f.bytes,
    pages: f.pages,
    whole: Boolean(f.whole),
    onDownload: () => onDownload(f.file),
  }));
  // A package printed before the whole-document file still has the draft's own PDF to offer.
  if (!pkg.files.some((f) => f.whole)) {
    rows.unshift({ key: "pdf", swatch: FILE_SWATCHES.uncategorized, label: "Whole document", caption: "All sections", overLimit: null, bytes: pdfBytes, pages: pdfPages, whole: true, onDownload: onDownloadPdf });
  }
  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== "backdropClick") onClose(); }} fullWidth maxWidth="sm">
      <DialogTitle>Download</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{`The whole document, or one file per ${providerLabel} evidence category. The total counts the files only.`}</Typography>
        {rows.map((r) => (
          <Box key={r.key} sx={{ display: "grid", gridTemplateColumns: "12px 1fr auto auto", columnGap: "12px", alignItems: "center", py: 1, borderTop: 1, borderColor: "divider", ...(r.whole && { bgcolor: "action.hover", borderRadius: 1, mx: -1, px: 1 }) }}>
            <Box sx={{ width: 12, height: 12, borderRadius: "2px", bgcolor: r.swatch }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.label}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">{r.caption}</Typography>
              {r.overLimit && <Typography variant="caption" color="error" display="block">{r.overLimit}</Typography>}
            </Box>
            <Typography variant="caption" color={r.overLimit ? "error" : "text.secondary"} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
              {[r.bytes != null && formatFileSize(r.bytes), r.pages != null && `${r.pages} ${r.pages === 1 ? "page" : "pages"}`].filter(Boolean).join(" · ")}
            </Typography>
            <Button size="small" variant="text" sx={{ textTransform: "none" }} onClick={r.onDownload}>Download</Button>
          </Box>
        ))}
        <Box sx={{ mt: 1.5 }}>
          {problems.length > 0
            ? <Alert severity="error">{problems.join(" ")}</Alert>
            : <Alert severity="success">{`Everything fits ${providerLabel}'s limits.`}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
